import {
  gaqlchainIs as chainIs,
  gaqlchainArgResIs as chainArgResIs,
  gaqlchainArgResResolve as chainArgResResolve
} from '../gaqlchain.js'

import {
  gaqlutilPlainObjIs as plainObjIs,
  gaqlutilNumBoolOrStrIs as numBoolOrStrIs
} from '../gaqlutil.js'

import {
  gaqlerrAttributeNotFound as errAttributeNotFound,
  gaqlerrArgsNthTypeMustBe as errArgsNthTypeMustBe,
  gaqlerrArgsNumber as errArgsNumber
} from '../gaqlerr.js'

import {
  gaqlenumARGSIG as ARGSIG
} from '../gaqlenum.js'

import {
  gaqlutilResStatesMerge as utilResStatesMerge
} from '../gaqlutil.js'

import {
  gaqltargetArgsCreate as targetArgsCreate
} from '../gaqltarget.js'

import {
  gaqlasyncarrReduce as asyncarrReduce
} from '../gaqlasyncarr.js'

// q.args is a special term that’s used to splice an array of arguments into
// another term. This is useful when you want to call a variadic term such as
// getAll with a set of arguments produced at runtime.
//
// This is analogous to using apply in JavaScript. (However, note that args
// evaluates all its arguments before passing them into the parent term, even
// if the parent term otherwise allows lazy evaluation.)
// ```
// q.args(array) → special
// ```
const $args = async (gst, qst, args, run) => {
  const result = await run(gst, qst, args[0])
  if (!Array.isArray(result))
    throw errArgsNthTypeMustBe('args', 0, 'array')

  qst.target = targetArgsCreate(result)

  return qst
}

// Run a query on a connection. The callback will get either an error,
// a single JSON result, or a cursor, depending on the query.
// ```
// query.run(conn[, options], callback)
// query.run(conn[, options]) → promise
// ```
const $run = (gst, qst) => {
  // if (qst.error)
  //   throw new Error(qst.error)

  return qst
}

// Returns the currently visited row/document.
//
// ```
// q.row → value
// q.row('age').gt(5)
// row => row('name_screenname')
// q.row('hobbies').add(q.row( 'sports'))
// ```
//
// dynamic row sometimes passes value,
//   [ 'reqlARGSSUSPEND', 'reqlARGSIG.row', 0, 'row' ]
//
// target row will include value in qst.target
const $row = async (gst, qst, args) => {
  const isARGSIG = args[0] === ARGSIG

  if (isARGSIG && !(args[1] in qst.rowMap)) {
    // keep this for development
    // console.log({t:qst.target,qrecId:qst.recId,args,qrowMap:qst.rowMap})
    throw new Error('[!!!] error: missing ARGS from ROWMAP')
  }

  qst.target = isARGSIG
    ? qst.rowMap[args[1]][args[2]]
    : qst.target[args[0]]

  return qst
}

// Provide a default value in case of non-existence errors. The default
// command evaluates its first argument (the value it’s chained to). If that
// argument returns null or a non-existence error is thrown in evaluation, then
// default returns its second argument. The second argument is usually a default
// value, but it can be a function that returns a value.
// ```
// value.default(default_value | function) → any
// sequence.default(default_value | function) → any
// ```
const $default = async (gst, qst, args, run) => {
  const target = qst.target

  // it is assumed some queries added externally may resolve targets that are
  // `null` or `undefined` and that such queries should resolve subsequent
  // "default()" query expressions
  if (target === null || target === undefined) {
    qst.error = null
    qst.target = await run(gst, qst, args[0])
  }

  return qst
}

// Construct a QL JSON object from a native object.
//
// ```
// q.expr({a:'b'}).merge({b:[1,2,3]})
// ```
const $expr = async (gst, qst, args, run) => {
  qst.target = await run(gst, qst, args[0], [qst.target])

  return qst
}

const $rowfn = $expr
const $bracket = $expr

// Call an anonymous function using return values from other
//
// ```
// any.do(function) → any
// q.do([args]*, function) → any
// any.do(expr) → any
// q.do([args]*, expr) → any
// ```
const $do = async (gst, qst, args, run) => {
  const doFn = args.slice(-1)[0]

  if (chainIs(doFn)) {
    qst.target = args.length === 1
      ? await run(gst, qst, doFn, [qst.target])
      : await run(gst, qst, doFn,
        await run(gst, qst, args.slice(0, -1)))

    // handles this sort of query
    // ```
    // q.do(q.args([q.expr(3), q.expr(4)]))
    //  -> { 'gn:ARGRES': [ 3, 4] }
    // ```
    if (chainArgResIs(qst.target))
      qst.target = chainArgResResolve(qst.target)[0]
  } else if (args.length) {
    qst.target = doFn
  }

  return qst
}


// Perform a branching conditional equivalent to if-then-else.
//
// The branch command takes 2n+1 arguments: pairs of conditional expressions
// and commands to be executed if the conditionals return any value but false
// or null (i.e., “truthy” values), with a final “else” command to be
// evaluated if all of the conditionals are false or null.
// ```
// q.branch(test, true_action[, test2, test2_action, ...], false_action) → any
// test.branch(true_action[, test2, test2_action, ...], false_action) → any
// ```
const $branch = async (gst, qst, args, run, recs, recNum) => {
  const nextCondition = async (condition, branches) => {
    const conditionResult = await run(gst, qst, condition)

    if (branches.length === 0)
      return conditionResult

    if (conditionResult) {
      return run(gst, qst, branches[0])
    }

    return nextCondition(branches[1], branches.slice(2))
  }

  // if 'branch' occurs at depth, assume 'infix' style
  // using first branch argument
  qst.target = recNum === 0
    ? await nextCondition(args[0], args.slice(1))
    : await nextCondition(qst.target, args.slice(0))

  return qst
}

// Loop over a sequence, evaluating the given write query for each element.
// ```
// sequence.forEach(write_function) → object
// ```
const $forEach = async (gst, qst, args, run) => {
  const forEachRow = args[0]

  if (args.length !== 1) {
    throw errArgsNumber('forEach', 1, args.length, 1)
  }

  qst.target = await asyncarrReduce(qst.target, async (ac, arg) => {
    const result = await run(gst, qst, forEachRow, [arg])

    // return result
    return utilResStatesMerge(ac, result)
  }, {})

  return qst
}

// Generate a stream of sequential integers in a specified range.
//
// range takes 0, 1 or 2 arguments:
//
//  * With no arguments *not supported here*, range returns an “infinite”
//    stream from 0 up to and including the maximum integer value;
//  * With one argument, range returns a stream from 0 up to but not
//    including the end value;
//  * With two arguments, range returns a stream from the start value up
//    to but not including the end value.
//
// Note that the left bound (including the implied left bound of 0 in the
// 0- and 1-argument form) is always closed and the right bound is always
// open: the start value will always be included in the returned range and
// the end value will not be included in the returned range.
// ```
// q.range() → stream
// q.range([startValue, ]endValue) → stream
// ```
const $range = async (gst, qst, args, run) => {
  const argslength = args.length

  if (1 > argslength || argslength > 2)
    throw errArgsNumber('range', 1, argslength, 2)

  const rangeNumBgn = argslength === 2
    ? await run(gst, qst, args[0]) : 0
  const rangeNumEnd = argslength === 2
    ? await run(gst, qst, args[1])
    : await run(gst, qst, args[0])
  const rangeLength = rangeNumEnd - rangeNumBgn

  qst.target = Array.from({length: rangeLength}, (_, i) => i + rangeNumBgn)

  return qst
}

// Throw a runtime error. If called with no arguments inside the second argument
// to default, re-throw the current error.
// ```
// q.error(message) → error
// ```
const $error = async (gst, qst, args, run) => {
  const error = await run(gst, qst, args[0])

  throw new Error(error)
}

// any.typeOf() → string
//
// Gets the type of a ReQL query’s return value.
//
// The type will be returned as a string:
// ARRAY, BOOL, DB, FUNCTION, GROUPED_DATA, GROUPED_STREAM
// MAXVAL, MINVAL, NULL, NUMBER, OBJECT, PTYPE<BINARY>
// PTYPE<GEOMETRY>, PTYPE<TIME>, SELECTION<ARRAY>
// SELECTION<OBJECT>, SELECTION<STREAM>, STREAM
// STRING, TABLE_SLICE, TABLE
const $typeOf = (gst, qst) => {
  const value = qst.target

  let t = null
  if (value === t)
    t = 'NULL'
  else if (numBoolOrStrIs(value))
    t = (typeof value).toUpperCase()
  else if (Array.isArray(value))
    t = 'ARRAY'
  else
    t = 'OBJECT'

  qst.target = t

  return qst
}

// Convert a value of one type into another.
//  * a sequence, selection or object can be coerced to an array
//  * a sequence, selection or an array of key-value pairs can be coerced
//    to an object
//  * a string can be coerced to a number
//  * any datum (single value) can be coerced to to a string
//  * a binary object can be coerced to a string and vice-versa
//
// ```
// sequence.coerceTo('array') → array
// value.coerceTo('string') → string
// string.coerceTo('number') → number
// array.coerceTo('object') → object
// sequence.coerceTo('object') → object
// object.coerceTo('array') → array
// binary.coerceTo('string') → string
// string.coerceTo('binary') → binary
// ```
const $coerceTo = async (gst, qst, args) => {
  const coerceType = String(args[0]).toLowerCase()
  const target = qst.target

  const coerceToString = t => String(t)

  // q.expr([['name', 'Ironman'], ['victories', 2000]])
  //  .coerceTo('object').run()
  const coerceToObject = t => {
    if (Array.isArray(t))
      return t.reduce((acc, k) => (
        acc[k[0]] = k[1],
        acc
      ), {})
  }

  if (coerceType === 'number')
    qst.target = Number.parseFloat(target)
  else if (coerceType === 'string')
    qst.target = coerceToString(target)
  else if (coerceType === 'object') {
    qst.target = coerceToObject(target)
  }

  return qst
}

// Return a UUID (universally unique identifier), a string that can be used as
// a unique ID. If a string is passed to uuid as an argument, the UUID will be
// deterministic, derived from the string’s SHA-1 hash.
//
// RethinkDB’s UUIDs are standards-compliant. Without the optional argument,
// a version 4 random UUID will be generated; with that argument, a version 5
// UUID will be generated, using a fixed namespace UUID of
// 91461c99-f89d-49d2-af96-d8e2e14e9b58.
// ```
// q.uuid([string]) → string
// ```
const $uuid = async (gst, qst) => {
  const envcrypto = crypto

  qst.target = envcrypto.randomUUID()

  return qst
}

// Get a single field from an object. If called on a sequence, gets that field
// from every object in the sequence, skipping objects that lack it.
//
// https://rethinkdb.com/api/javascript/get_field
// ```
// sequence.getField(attr) → sequence
// singleSelection.getField(attr) → value
// object.getField(attr) → value
// ```
const $getField = async (gst, qst, args, run) => {
  const target = qst.target
  const targetIsObj = plainObjIs(target)
  const targetIsArray = Array.isArray(target)
  const fieldName = await run(gst, qst, args[0])

  if (args.length === 0)
    throw errArgsNumber('getField', 1, args.length, 1)
  if (!targetIsObj && !targetIsArray)
    throw errAttributeNotFound(fieldName, target)
  if (targetIsObj && (fieldName in target === false))
    throw errAttributeNotFound(fieldName, target)

  if (qst.isGrouped) {
    qst.target = target.map(targetGroup => {
      targetGroup.reduction = targetGroup.reduction[fieldName]

      return targetGroup
    })
  } else {
    qst.target = targetIsArray
      ? target.map(t => t[fieldName])
      : target[fieldName]
  }

  return qst
}

export {
  $args,
  $run,
  $row,
  $default,
  $do,
  $branch,
  $forEach,
  $range,
  $error,
  $expr,
  $rowfn,
  $bracket,
  $coerceTo,
  $typeOf,
  $getField,
  $uuid
}
